import React from 'react'
import styled, { css } from 'styled-components'
import themeColors from '@layers-frontend/commons/styles/themeColors'

import noop from 'lodash/noop'

// Pills for the actions of forms and modals. Only the surface changes, so each button keeps its own size: confirm is
// solid mint with a soft ring on hover, cancel deep navy with a mint contour that turns red, neutral the same navy pill
// that lightens instead (a cancel beside a destructive action, so red stays with that action), outline a mint contour
// that fills mint, and danger solid red with a red ring.
const pillShape = css`
  border-radius: 999px;
  font-style: normal;
  transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;
`

const navyPill = css`
  ${pillShape};
  background-color: ${themeColors.deepblueColor3};
  color: ${themeColors.whiteTransparent08};
  box-shadow: inset 0 0 0 1px ${themeColors.mintBorder};
`

export const pillLooks = {
  confirm: css`
    ${pillShape};
    background-color: ${themeColors.mintColor};
    color: ${themeColors.deepblueColor3};
    box-shadow: none;
    &:hover:not(:disabled) {
      box-shadow: 0 0 0 3px ${themeColors.mintColorTransparent};
    }
  `,
  cancel: css`
    ${navyPill};
    &:hover:not(:disabled) {
      background-color: ${themeColors.redColor};
      color: ${themeColors.whiteColor};
      box-shadow: inset 0 0 0 1px ${themeColors.redColor};
    }
  `,
  neutral: css`
    ${navyPill};
    &:hover:not(:disabled) {
      color: ${themeColors.whiteColor};
      box-shadow: inset 0 0 0 1px ${themeColors.mintBorder}, inset 0 0 0 999px ${themeColors.whiteTransparent01};
    }
  `,
  outline: css`
    ${pillShape};
    background-color: ${themeColors.transparent};
    color: ${themeColors.whiteColor};
    box-shadow: inset 0 0 0 1px ${themeColors.mintColor};
    &:hover:not(:disabled) {
      background-color: ${themeColors.mintColor};
      color: ${themeColors.deepblueColor3};
    }
  `,
  danger: css`
    ${pillShape};
    background-color: ${themeColors.redColor};
    color: ${themeColors.whiteColor};
    box-shadow: none;
    &:hover:not(:disabled) {
      box-shadow: 0 0 0 3px ${themeColors.dangerRedTransparent};
    }
  `
}

const StyledButton = styled.button`
  color: ${props => (props.color ? props.color : themeColors.darkBlue)};
  border: none;
  background-color: ${props => (props.background ? props.background : themeColors.mintColor)};
  opacity: ${props => (props.disabled ? 0.4 : 1)};
  padding: 1rem 1.5rem;
  font-size: 1.3rem;
  font-weight: 400;
  border-radius: 0.3rem;
  line-height: 1;
  i {
    margin-left: 0.5rem;
  }
  ${props =>
    props.text &&
    css`
      display: inline-flex;
      text-decoration-line: none;
      padding-left: 0;
      &:hover {
        text-decoration-line: underline;
      }
    `};
  ${props =>
    props.outlined &&
    css`
      background-color: transparent;
      box-shadow: inset 0 0 0 1px ${props.color || themeColors.whiteColor};
    `};
  ${props => pillLooks[props.pill]};
`

export const Button = ({ label, onClick = noop, background, color, icon, disabled, text, outlined, pill, testId }) => (
  <StyledButton
    onClick={e => {
      e.preventDefault()
      onClick()
    }}
    background={background}
    color={color}
    disabled={disabled}
    text={text}
    outlined={outlined}
    pill={pill}
    data-testid={testId}
  >
    {label}
    {icon && <i className={icon} />}
  </StyledButton>
)

export const TextButton = props => <Button {...props} background={themeColors.transparent} text />
